import gql from 'graphql-tag';
import React from 'react';
import { Query, Mutation } from 'react-apollo';
import { RouteComponentProps } from 'react-router';
import { Button } from '../../../components/Bootstrap';
import { transformAssignmentResponse, selectedToState } from '@getnoble/noble-tip-splitting-utils';
import { Layout, LayoutContent, Navigation } from '../../../components';
import { DeliveryLayoutRow, DeliverySeparator } from '../components';
import { serverAssignmentsFragment } from '../../../fragments';
import { DeliveryLocationFields } from '../../../fragments/__generated__/DeliveryLocationFields';
import {
  EditAssignmentsQuery,
  EditAssignmentsQueryVariables,
  EditAssignmentsQuery_serverAssignments_assignments as Assignment,
} from './__generated__/EditAssignmentsQuery';
import { RemoveAssignments, RemoveAssignmentsVariables } from './__generated__/RemoveAssignments';

interface EditAssignmentsRouterParams {
  venueId: string;
  fulfillmentCenterId: string;
  serverId: string;
}

type RouterProps = RouteComponentProps<EditAssignmentsRouterParams>;

interface EditAssignmentsProps extends RouterProps {
  data?: EditAssignmentsQuery;
  loading: boolean;
  error?: Error;
  venueId: string;
  fulfillmentCenterId: string;
  updateAssignments(options: { variables: RemoveAssignmentsVariables });
}

interface EditAssignmentsState {
  selected: Assignment[];
}

class EditAssignmentsUnwrapped extends React.Component<EditAssignmentsProps, EditAssignmentsState> {
  state = {
    selected: [],
  };

  constructor(props: EditAssignmentsProps) {
    super(props);

    if (props.data) {
      this.state = {
        selected: props.data.serverAssignments.assignments,
      };
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.data !== this.props.data) {
      this.setState({
        selected: this.props.data.serverAssignments.assignments,
      });
    }
  }

  onToggle = (location: DeliveryLocationFields) => {
    if (this.state.selected.find((a) => a.location.id === location.id)) {
      this.setState({
        selected: this.state.selected.filter((a) => a.location.id !== location.id),
      });
    } else {
      const assignment = this.props.data.serverAssignments.assignments.find((a) => {
        return (a.location.id = location.id);
      });

      this.setState({
        selected: this.state.selected.concat([assignment]),
      });
    }
  };

  onClickUpdate = async () => {
    const locationIdsToRemove = this.props.data.serverAssignments.assignments
      .filter((assignment) => {
        return !this.state.selected.includes(assignment);
      })
      .map((assignment) => assignment.location.id);

    const { venueId, fulfillmentCenterId, serverId } = this.props.match.params;

    await this.props.updateAssignments({
      variables: {
        venueId,
        serverId,
        fulfillmentCenterId,
        locationIdsToRemove,
      },
    });

    await this.props.history.push(
      `/venue/${venueId}/fulfillment-centers/${fulfillmentCenterId}/assignments/${serverId}`
    );
  };

  renderContent() {
    if (this.props.loading || !this.props.data) {
      return <p>Loading...</p>;
    } else {
      const { venueId, fulfillmentCenterId, data } = this.props;
      const transformedData = transformAssignmentResponse(data.serverAssignments.assignments);

      const currentSelections = this.state.selected.map((section) => section.location.name).join(', ');
      const addUrl = `/venue/${venueId}/fulfillment-centers/${fulfillmentCenterId}/assignments/${data.serverAssignments.server.id}/add`;

      return (
        <div className='delivery-assignments'>
          <h3 className='mb-2'>{data.serverAssignments.server.name}</h3>
          <div>
            <Button color='primary' onClick={this.onClickUpdate} className='btn--rounded-padded float-right'>
              Save
            </Button>
            <Button color='link' className='float-right'>
              Cancel
            </Button>
            <p className='m-0'>
              <b>Selected:</b> {currentSelections}
            </p>
            <p>
              <Button className='p-0' color='link'>
                Deselect All
              </Button>
            </p>
          </div>
          <DeliverySeparator className='delivery-assignments-separator' />
          {transformedData.map((row) => (
            <DeliveryLayoutRow
              fulfillmentCenterName={data.fulfillmentCenter.name}
              key={`edit-${row.id}`}
              selectedStatus={selectedToState(this.state.selected)}
              locations={row.locations}
              onToggle={this.onToggle}
              parentNames={row.parentNames}
              showHelpText={false}
            />
          ))}
          <div>
            <Button color='outline-primary' href={addUrl} className='btn--rounded-padded'>
              Add Locations
            </Button>
          </div>
        </div>
      );
    }
  }

  render() {
    return (
      <Layout>
        <Navigation />
        <Layout.TopBar>
          <Layout.TopBar.Left>
            <Layout.TopBar.Title>Fulfillment Stations</Layout.TopBar.Title>
          </Layout.TopBar.Left>
        </Layout.TopBar>
        <LayoutContent error={this.props.error}>
          <div>{this.renderContent()}</div>
        </LayoutContent>
      </Layout>
    );
  }
}

const query = gql`
  query EditAssignmentsQuery($venueId: ID!, $fulfillmentCenterId: ID!, $serverId: ID!) {
    fulfillmentCenter(venueId: $venueId, fulfillmentCenterId: $fulfillmentCenterId) {
      id
      name
    }
    serverAssignments(venueId: $venueId, fulfillmentCenterId: $fulfillmentCenterId, serverId: $serverId) {
      ...ServerAssignmentFields
    }
  }
  ${serverAssignmentsFragment}
`;

const updateAssignemnts = gql`
  mutation RemoveAssignments($venueId: ID!, $serverId: ID!, $fulfillmentCenterId: ID!, $locationIdsToRemove: [ID!]!) {
    removeAssignments(
      venueId: $venueId
      serverId: $serverId
      fulfillmentCenterId: $fulfillmentCenterId
      locationIds: $locationIdsToRemove
    )
  }
`;

export const EditAssignments = (props: RouterProps) => {
  const { venueId, fulfillmentCenterId, serverId } = props.match.params;

  return (
    <Mutation<RemoveAssignments, RemoveAssignmentsVariables> mutation={updateAssignemnts}>
      {(updateAssignments, updateAssignmentData) => (
        <Query<EditAssignmentsQuery, EditAssignmentsQueryVariables>
          variables={{ venueId, fulfillmentCenterId, serverId }}
          query={query}
          fetchPolicy='no-cache'
        >
          {({ loading, error, data }) => {
            return (
              <EditAssignmentsUnwrapped
                {...props}
                loading={loading || updateAssignmentData.loading}
                error={error}
                data={data}
                venueId={venueId}
                fulfillmentCenterId={fulfillmentCenterId}
                updateAssignments={updateAssignments}
              />
            );
          }}
        </Query>
      )}
    </Mutation>
  );
};
